import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { SeasonModel } from 'models/seasons';
import { SeasonStore } from 'store';

@Component({
    selector: 'app-season-select',
    templateUrl: './season-select.component.html',
    styleUrls: ['./season-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatMenuTrigger, MatMenu, MatMenuItem, TranslateModule, MatIconModule],
})
export class SeasonSelectComponent {
    private readonly seasonStore = inject(SeasonStore);

    // Input signals
    public activeSeason = input<SeasonModel>();
    public seasons = input<Array<SeasonModel>>();

    // Computed signals
    public main = computed(() => {
        const seasonsValue = this.seasons();
        return seasonsValue ? seasonsValue.slice(0, 5) : [];
    });

    public older = computed(() => {
        const seasonsValue = this.seasons();
        return seasonsValue ? seasonsValue.slice(5) : [];
    });

    public hasPreviousSeason = computed(() => {
        const activeSeason = this.activeSeason();
        const allSeasons = this.seasons();

        if (!activeSeason || !allSeasons?.length) {
            return false;
        }

        const currentIndex = allSeasons.findIndex((s) => s.id === activeSeason.id);
        return currentIndex > 0;
    });

    public hasNextSeason = computed(() => {
        const activeSeason = this.activeSeason();
        const allSeasons = this.seasons();

        if (!activeSeason || !allSeasons?.length) {
            return false;
        }

        const currentIndex = allSeasons.findIndex((s) => s.id === activeSeason.id);
        return currentIndex >= 0 && currentIndex < allSeasons.length - 1;
    });

    public selectSeason(s: SeasonModel) {
        this.seasonStore.change(s);
    }

    public goToPreviousSeason(): void {
        if (!this.hasPreviousSeason()) {
            return;
        }

        const activeSeason = this.activeSeason();
        const allSeasons = this.seasons();

        if (!activeSeason || !allSeasons) {
            return;
        }

        const currentIndex = allSeasons.findIndex((s) => s.id === activeSeason.id);
        const previousSeason = allSeasons[currentIndex - 1];
        this.selectSeason(previousSeason);
    }

    public goToNextSeason(): void {
        if (!this.hasNextSeason()) {
            return;
        }

        const activeSeason = this.activeSeason();
        const allSeasons = this.seasons();

        if (!activeSeason || !allSeasons) {
            return;
        }

        const currentIndex = allSeasons.findIndex((s) => s.id === activeSeason.id);
        const nextSeason = allSeasons[currentIndex + 1];
        this.selectSeason(nextSeason);
    }
}
